import { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Send, MessageCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * Reusable chat drawer.
 * Modes:
 *  - mode="direct": chat with a user (needs toUserId + optional adId)
 *  - mode="order":  chat inside a service order (needs orderId)
 */
export const ChatDrawer = ({ open, onOpenChange, title, mode = 'direct', toUserId, adId, orderId, counterpartLabel }) => {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [threadId, setThreadId] = useState(null);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    const load = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        try {
            if (mode === 'order' && orderId) {
                const { data } = await api.get(`/services/orders/${orderId}/messages`);
                setMessages(data.messages || []);
                setThreadId(data.thread_id);
            } else if (mode === 'direct' && toUserId) {
                // find existing thread with this user + adId
                const { data } = await api.get('/me/threads');
                const t = (data || []).find(t => t.participants?.includes(toUserId) && (t.ad_id === (adId || null) || (!t.ad_id && !adId)));
                if (t) {
                    setThreadId(t.id);
                    const { data: r } = await api.get(`/messages/${t.id}`).catch(() => ({ data: null }));
                    setMessages(r?.messages || []);
                } else {
                    setThreadId(null);
                    setMessages([]);
                }
            }
        } catch (e) {
            // silently handle 404 for new threads
        } finally { setLoading(false); }
    }, [open, mode, orderId, toUserId, adId]);

    useEffect(() => { load(); }, [load]);

    // scroll to bottom when messages change
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const send = async () => {
        const t = text.trim();
        if (!t) return;
        setBusy(true);
        try {
            if (mode === 'order') {
                const { data } = await api.post(`/services/orders/${orderId}/messages`, { text: t });
                setMessages(m => [...m, data.message]);
                setThreadId(data.thread_id);
            } else {
                if (threadId) {
                    const { data } = await api.post('/messages/reply', { thread_id: threadId, text: t });
                    setMessages(m => [...m, data.message || data]);
                } else {
                    const { data } = await api.post('/messages', { to_user_id: toUserId, ad_id: adId || null, text: t });
                    setMessages([data.message]);
                    setThreadId(data.thread_id);
                }
            }
            setText('');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al enviar'); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg p-0 gap-0 flex flex-col h-[600px] max-h-[85vh]" data-testid="chat-drawer">
                <DialogHeader className="p-4 border-b">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <MessageCircle className="h-4 w-4" />
                        {title || (counterpartLabel ? `Chat con @${counterpartLabel}` : 'Mensajes')}
                    </DialogTitle>
                </DialogHeader>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[hsl(var(--secondary))]/30">
                    {loading && <div className="text-center text-xs text-muted-foreground">Cargando…</div>}
                    {!loading && messages.length === 0 && (
                        <div className="text-center text-sm text-muted-foreground py-8">
                            Envía el primer mensaje para iniciar la conversación.
                        </div>
                    )}
                    {messages.map((m, i) => {
                        const mine = m.from_user_id === user?.id;
                        return (
                            <div key={m.id || i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? 'bg-[hsl(var(--primary))] text-white rounded-br-sm' : 'bg-card border rounded-bl-sm'}`}>
                                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                    <div className={`text-[9px] mt-0.5 ${mine ? 'text-white/70' : 'text-muted-foreground'}`}>
                                        {m.created_at ? new Date(m.created_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : ''}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="p-3 border-t flex gap-2">
                    <Input
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder="Escribe un mensaje…"
                        data-testid="chat-input"
                    />
                    <Button onClick={send} disabled={busy || !text.trim()} data-testid="chat-send">
                        <Send className="h-4 w-4" />
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ChatDrawer;
